/**
 * Favourites page
 *
 * Flow:
 *  1) GET /apps/wishlist/api/items  →  [{ productId, ... }]
 *  2) Call backend API (API_BASE_URL) to resolve product IDs → handles
 *       GET {API_BASE_BASE}/shopify/product-handles?ids=123,456
 *     (backend uses Storefront/Admin API server-side)
 *  3) For each handle, GET /products/{handle}?sections=favourites-card
 *     and append the returned HTML to the grid.
 *  4) Support \"Load more\" in batches of 20, using the same Section Rendering API.
 */
(function () {
  'use strict';

  var BATCH_SIZE = 40;
  var CONCURRENT_REQUESTS = 10;
  var STORAGE_KEY_HANDLES = 'favourites-handles';
  var APPMATE_API_HOST = 'https://api.appmate.io/v2';

  // ---------------------------------------------------------------------------
  // DOM helpers
  // ---------------------------------------------------------------------------

  function getSectionRoot() {
    return document.querySelector('[id^="favourites-section-"]');
  }

  function getSectionId() {
    var root = getSectionRoot();
    return root ? root.dataset.sectionId : null;
  }

  function getEl(idSuffix) {
    var sectionId = getSectionId();
    if (!sectionId) return null;
    return document.getElementById('favourites-' + idSuffix + '-' + sectionId);
  }

  function showEl(el, show) {
    if (!el) return;
    el.classList.toggle('hidden', !show);
    el.setAttribute('aria-hidden', show ? 'false' : 'true');
  }

  // ---------------------------------------------------------------------------
  // API helpers (mirror recommendations.js pattern)
  // ---------------------------------------------------------------------------

  function getApiBase() {
    return window.API_BASE_URL
      ? window.API_BASE_URL.replace(/\/shopify\/?$/, '')
      : '';
  }

  function refreshIdToken() {
    return new Promise(function (resolve, reject) {
      var auth = window.firebaseAuth;
      if (!auth || typeof auth.waitForAuthState !== 'function') {
        reject(new Error('Firebase auth not available'));
        return;
      }
      auth.waitForAuthState(3000).then(function (user) {
        if (!user) {
          reject(new Error('No Firebase user'));
          return;
        }
        user.getIdToken(true).then(function (newToken) {
          try {
            sessionStorage.setItem('firebaseIdToken', newToken);
          } catch (e) {
            console.error('[favourites] error setting firebase id token', e);
          }
          resolve(newToken);
        }).catch(reject);
      }).catch(reject);
    });
  }

  function apiFetch(url, options) {
    options = options || {};
    var token = null;
    try {
      token = sessionStorage.getItem('firebaseIdToken');
    } catch (e) {
      console.error('[favourites] error getting firebase id token', e);
    }

    var headers = Object.assign(
      { 'Content-Type': 'application/json' },
      options.headers || {},
      token ? { 'Authorization': 'Bearer ' + token } : {}
    );

    return fetch(url, Object.assign({}, options, { headers: headers }))
      .then(function (response) {
        if (response.status !== 401) return response;
        // Try to refresh token on specific auth error
        return response.clone().json().then(function (body) {
          if (!body || !body.message || body.message.indexOf('Expired or invalid id token') === -1) {
            return response;
          }
          return refreshIdToken().then(function (freshToken) {
            headers['Authorization'] = 'Bearer ' + freshToken;
            return fetch(url, Object.assign({}, options, { headers: headers }));
          }).catch(function () {
            return response;
          });
        }).catch(function () {
          return response;
        });
      });
  }

  function fetchWishlistItems(apiPath) {
    return fetch(apiPath, {
      method: 'GET',
      credentials: 'include',
      headers: { Accept: 'application/json' }
    }).then(function (res) {
      if (!res.ok) return null;
      var ct = res.headers.get('Content-Type') || '';
      if (ct.indexOf('application/json') === -1) return null;
      return res.json();
    }).catch(function () { return null; });
  }

  /**
   * Fallback for cases where the /items proxy returns an empty or legacy shape,
   * e.g. anonymous/session-based wishlists. Uses the Appmate /wishlists/mine
   * endpoint which reflects the current "mine" wishlist (ids + variants).
   */
  function fetchWishlistMine() {
    return fetch(APPMATE_API_HOST + '/wishlists/mine', {
      method: 'GET',
      headers: getWishlistShareRequestHeaders()
    }).then(function (res) {
      if (!res || !res.ok) return null;
      var ct = res.headers && res.headers.get ? (res.headers.get('Content-Type') || '') : '';
      if (ct.indexOf('application/json') === -1) return null;
      return res.json();
    }).catch(function () { return null; });
  }

  function extractWishlistItems(data) {
    if (!data || !Array.isArray(data.data)) return [];
    return data.data.filter(function (item) { return !!item; });
  }

  function getCartVariantIdSet() {
    return fetch('/cart.js', {
      credentials: 'same-origin',
      headers: { 'Cache-Control': 'no-cache' }
    })
      .then(function (res) {
        return res.ok ? res.json() : { items: [] };
      })
      .then(function (cart) {
        var ids = new Set();
        var items = cart && Array.isArray(cart.items) ? cart.items : [];
        items.forEach(function (item) {
          var variantId = item && (item.id || item.variant_id);
          if (variantId) ids.add(String(variantId));
        });
        return ids;
      })
      .catch(function () {
        return new Set();
      });
  }

  function addVariantToCart(variantId, quantity) {
    return fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'same-origin',
      body: JSON.stringify({
        items: [{ id: variantId, quantity: quantity || 1 }]
      })
    })
      .then(function (res) {
        return res.text().then(function (raw) {
          var json = null;
          try {
            json = JSON.parse(raw);
          } catch (e) {
            console.error('[favourites] error parsing JSON', e);
          }

          return {
            ok: res.ok,
            status: res.status,
            json: json,
            raw: raw
          };
        });
      })
      .catch(function (error) {
        return {
          ok: false,
          status: 0,
          json: null,
          raw: '',
          error: error
        };
      });
  }

  function extractProductIds(data) {
    if (!data) return [];

    // /apps/wishlist/api/items proxy format: { data: [...] }
    if (Array.isArray(data.data)) {
      return data.data.map(function (item) {
        return item && item.productId;
      }).filter(Boolean);
    }

    // /v2/wishlists/mine format:
    // { wishlist: { items: [{ productId, variantId, ... }, ...] } }
    if (data.wishlist && Array.isArray(data.wishlist.items)) {
      return data.wishlist.items.map(function (item) {
        return item && item.productId;
      }).filter(Boolean);
    }

    return [];
  }

  /**
   * Ask our backend to resolve product IDs to handles.
   * Expected response shape:
   *   { handles: [\"handle-1\", \"handle-2\", ...] }
   * or directly [\"handle-1\", ...]
   */
  function fetchHandlesFromBackend(productIds) {
    if (!productIds.length) return Promise.resolve([]);
    var base = getApiBase();
    if (!base) return Promise.resolve([]);

    var url = base + '/shopify/product-handles?ids=' + encodeURIComponent(productIds.join(','));

    console.debug('[favourites] fetching handles from backend', {
      url: url,
      count: productIds.length
    });

    return apiFetch(url, { method: 'GET' })
      .then(function (res) {
        console.debug('[favourites] backend status', res && res.status);
        if (!res.ok) return [];
        return res.json();
      })
      .then(function (json) {
        console.debug('[favourites] backend json', json);
        if (!json) return [];
        var handles = [];
        if (Array.isArray(json)) {
          handles = json.filter(function (h) { return typeof h === 'string' && h; });
        } else if (Array.isArray(json.handles)) {
          handles = json.handles.filter(function (h) { return typeof h === 'string' && h; });
        }
        console.log('[favourites] backend handles order (display order):', handles.map(function (h, i) { return i + ' → ' + h; }));
        return handles;
      })
      .catch(function () { return []; });
  }

  // ---------------------------------------------------------------------------
  // Section Rendering helpers
  // ---------------------------------------------------------------------------

  /**
   * Returns true if the card HTML looks like a valid product card (has real content).
   * Filters out placeholder/unavailable products that render with no image.
   */
  function isValidCardHtml(html) {
    if (!html || typeof html !== 'string') return false;
    var trimmed = html.trim();
    if (trimmed.length < 100) return false;
    // Real product cards include an image with src/srcset; unavailable/placeholder often have blank image area (no img or no source)
    var hasImg = trimmed.indexOf('<img') !== -1;
    var hasSource = trimmed.indexOf('src=') !== -1 || trimmed.indexOf('srcset=') !== -1;
    return hasImg && hasSource;
  }

  function getLocaleRoot() {
    var root = getSectionRoot();
    if (!root) return '';
    var localeRoot = root.getAttribute('data-locale-root') || '';
    // Shopify's locale root is "" for default, or like "/en". Treat "/" as empty.
    if (localeRoot === '/') return '';
    return localeRoot;
  }

  function renderCardHtml(handle, cardSectionName) {
    // Mirror recommendations.js: relative URL, Section Rendering API, but respect locale root (e.g. "/en").
    var localeRoot = getLocaleRoot();
    var prefix = localeRoot || '';
    var url = prefix + '/products/' + encodeURIComponent(handle) + '?sections=' + encodeURIComponent(cardSectionName);
    console.debug('[favourites] renderCardHtml fetch', url);
    return fetch(url)
      .then(function (res) {
        console.debug('[favourites] renderCardHtml status', handle, res && res.status);
        return res.ok ? res.json() : null;
      })
      .then(function (data) {
        if (!data || !data[cardSectionName]) {
          console.warn('[favourites] no section html for handle', handle, data);
          return null;
        }
        return data[cardSectionName];
      })
      .catch(function (err) {
        console.error('[favourites] renderCardHtml error', handle, err);
        return null;
      });
  }

  /**
   * Fetch card HTML for handles in parallel batches, then append to grid in
   * the same order as the handles array (backend order).
   */
  function appendCardsToGrid(handles, cardSectionName, gridEl) {
    if (!gridEl || !handles.length) return Promise.resolve();
    console.debug('[favourites] appending cards', { count: handles.length });
    var startIndex = 0;
    function processNextBatch() {
      var batch = handles.slice(startIndex, startIndex + CONCURRENT_REQUESTS);
      if (!batch.length) return Promise.resolve();
      var batchStart = startIndex;
      startIndex += batch.length;
      var promises = batch.map(function (handle, i) {
        return renderCardHtml(handle, cardSectionName).then(function (html) {
          return { index: batchStart + i, html: html };
        });
      });
      return Promise.all(promises).then(function (results) {
        results.sort(function (a, b) { return a.index - b.index; });
        results.forEach(function (r) {
          if (r.html && isValidCardHtml(r.html)) {
            var li = document.createElement('li');
            li.className = 'grid__item';
            li.innerHTML = r.html;
            gridEl.appendChild(li);
          }
        });
        return processNextBatch();
      });
    }
    return processNextBatch();
  }

  // ---------------------------------------------------------------------------
  // Main loader and refresh
  // ---------------------------------------------------------------------------

  /**
   * Fetch wishlist, resolve handles, render first batch and update UI.
   * Used by both initial load and refresh-after-remove.
   */
  function runFetchAndRender(root) {
    var cardSectionName = root.dataset.cardSectionName || 'favourites-card';
    var wishlistPath = root.dataset.wishlistApiPath || '/apps/wishlist/api/items';
    var loadingEl = getEl('loading');
    var contentEl = getEl('content');
    var emptyEl = getEl('empty');
    var gridWrapper = getEl('grid-wrapper');
    var gridEl = getEl('grid');
    var loadMoreWrapper = getEl('load-more-wrapper');
    var loadMoreBtn = getEl('load-more');
    if (!gridEl) return;

    function handleProductIds(productIds) {
      if (!productIds || productIds.length === 0) {
        showEl(loadingEl, false);
        showEl(contentEl, false);
        showEl(emptyEl, true);
        showEl(gridWrapper, false);
        showEl(loadMoreWrapper, false);
        return;
      }

      fetchHandlesFromBackend(productIds).then(function (handles) {
        if (handles.length === 0) {
          showEl(loadingEl, false);
          showEl(contentEl, false);
          showEl(emptyEl, true);
          showEl(gridWrapper, false);
          showEl(loadMoreWrapper, false);
          return;
        }

        try {
          sessionStorage.setItem(STORAGE_KEY_HANDLES, JSON.stringify(handles));
        } catch (e) {
          console.error('[favourites] error setting stored handles', e);
        }

        var firstBatch = handles.slice(0, BATCH_SIZE);
        var hasMore = handles.length > BATCH_SIZE;

        appendCardsToGrid(firstBatch, cardSectionName, gridEl).then(function () {
          showEl(loadingEl, false);
          showEl(contentEl, true);
          showEl(emptyEl, false);
          showEl(gridWrapper, true);
          showEl(loadMoreWrapper, hasMore);
        });

        if (loadMoreBtn) {
          loadMoreBtn.onclick = function () {
            var stored = null;
            try {
              stored = sessionStorage.getItem(STORAGE_KEY_HANDLES);
            } catch (e) {
              console.error('[favourites] error getting stored handles', e);
            }
            var allHandles = stored ? JSON.parse(stored) : [];
            var currentCount = gridEl.querySelectorAll('.grid__item').length;
            var nextBatch = allHandles.slice(currentCount, currentCount + BATCH_SIZE);
            if (nextBatch.length === 0) {
              showEl(loadMoreWrapper, false);
              return;
            }
            loadMoreBtn.disabled = true;
            appendCardsToGrid(nextBatch, cardSectionName, gridEl).then(function () {
              loadMoreBtn.disabled = false;
              var newCount = gridEl.querySelectorAll('.grid__item').length;
              showEl(loadMoreWrapper, newCount < allHandles.length);
            });
          };
        }
      });
    }

    // Customer: use /items (app proxy). No customer: use /mine with session id.
    if (window.__wishlistShareCustomerId) {
      fetchWishlistItems(wishlistPath)
        .then(function (data) { handleProductIds(extractProductIds(data)); })
        .catch(function () { handleProductIds([]); });
    } else {
      fetchWishlistMine()
        .then(function (data) { handleProductIds(extractProductIds(data)); })
        .catch(function () { handleProductIds([]); });
    }
  }

  /**
   * Remove a single card from the favourites grid (no API call). Used when the user
   * unselects an item so we don't rely on the wishlist API having updated yet.
   */
  function removeCardFromFavouritesGrid(buttonElement) {
    var gridItem = buttonElement.closest('.grid__item');
    if (!gridItem || !gridItem.parentElement) return;

    var productLink = gridItem.querySelector('a[href*="/products/"]');
    var handle = null;
    if (productLink && productLink.href) {
      var pathname = productLink.pathname || '';
      var match = pathname.match(/\/products\/([^/]+)/);
      if (match) handle = match[1];
    }

    gridItem.remove();

    if (handle) {
      try {
        var stored = sessionStorage.getItem(STORAGE_KEY_HANDLES);
        var handles = stored ? JSON.parse(stored) : [];
        if (Array.isArray(handles)) {
          handles = handles.filter(function (h) { return h !== handle; });
          sessionStorage.setItem(STORAGE_KEY_HANDLES, JSON.stringify(handles));
        }
      } catch (e) {
        console.error('[favourites] error removing card from favourites grid', e);
      }
    }

    var gridEl = getEl('grid');
    var emptyEl = getEl('empty');
    var gridWrapper = getEl('grid-wrapper');
    var loadMoreWrapper = getEl('load-more-wrapper');
    var remaining = gridEl ? gridEl.querySelectorAll('.grid__item').length : 0;

    if (remaining === 0) {
      showEl(gridWrapper, false);
      showEl(emptyEl, true);
      showEl(loadMoreWrapper, false);
    } else {
      try {
        stored = sessionStorage.getItem(STORAGE_KEY_HANDLES);
        handles = stored ? JSON.parse(stored) : [];
        showEl(loadMoreWrapper, remaining < handles.length);
      } catch {
        showEl(loadMoreWrapper, false);
      }
    }

    if (window.suppressFavouritesToastOnce) {
      window.suppressFavouritesToastOnce = false;
      return;
    }
    var message = (typeof window.favouritesToastRemoved === 'string' && window.favouritesToastRemoved)
      ? window.favouritesToastRemoved
      : 'Removed from favourites';
    if (typeof window.showConfirmationToast === 'function') {
      window.showConfirmationToast(message);
    }
  }

  /**
   * Called after a successful add-to-cart from the favourites page. Removes the item from
   * wishlist via the wk-button (so it persists) and from the DOM, without showing the
   * "Removed from favourites" toast. Call with the grid item element that was added to cart.
   */
  function removeFavouriteOnCartAddSuccess(gridItem) {
    if (!gridItem || !gridItem.querySelector) return;
    var wkButton = gridItem.querySelector('.wk-button.wk-selected');
    if (!wkButton) return;
    window.suppressFavouritesToastOnce = true;
    try {
      wkButton.click();
    } catch {
      window.suppressFavouritesToastOnce = false;
    }
  }

  window.removeFavouriteOnCartAddSuccess = removeFavouriteOnCartAddSuccess;

  var wishlistPublicIdPromise = null;

  function getWishlistShareRequestHeaders() {
    var headers = {
      Accept: 'application/json',
      'Content-Type': 'application/json',
      'X-Appmate-Src': 'wishlist',
      'X-Appmate-Vca': 'true',
      'X-Appmate-Vcm': 'true'
    };

    if (window.__wishlistShareCustomerId) {
      headers['X-Appmate-Cid'] = String(window.__wishlistShareCustomerId);
    }

    // When the user is not logged into Shopify, Swish/Wishlist King identifies
    // the current wishlist by a session id stored in localStorage. Mirror the
    // app's behaviour so that /v2/wishlists/mine returns the same items that
    // the /apps/wishlist UI shows.
    try {
      if (window.localStorage) {
        var wkSessionId = window.localStorage.getItem('wk_session_id');
        if (wkSessionId) {
          headers['X-Appmate-Sid'] = wkSessionId;
        }
      }
    } catch {
      // Ignore storage errors (Safari ITP, disabled storage, etc.)
    }

    if (window.Shopify && window.Shopify.shop) {
      headers['X-Appmate-Shp'] = window.Shopify.shop;
    }

    return headers;
  }

  function removeWishlistItemById(wishlistItemId) {
    if (!wishlistItemId) return Promise.resolve(false);

    return fetch(APPMATE_API_HOST + '/wishlists/mine/items', {
      method: 'DELETE',
      headers: getWishlistShareRequestHeaders(),
      body: JSON.stringify({ wishlistItemId: wishlistItemId })
    })
      .then(function (res) {
        return res.ok;
      })
      .catch(function () {
        return false;
      });
  }

  function clearWishlist() {
    return fetch(APPMATE_API_HOST + '/wishlists/mine', {
      method: 'DELETE',
      headers: getWishlistShareRequestHeaders()
    })
      .then(function (res) {
        return res.ok;
      })
      .catch(function () {
        return false;
      });
  }

  function showFavouritesEmptyState() {
    var loadingEl = getEl('loading');
    var contentEl = getEl('content');
    var emptyEl = getEl('empty');
    var gridWrapper = getEl('grid-wrapper');
    var loadMoreWrapper = getEl('load-more-wrapper');
    var gridEl = getEl('grid');

    if (gridEl) {
      gridEl.innerHTML = '';
    }

    try {
      sessionStorage.removeItem(STORAGE_KEY_HANDLES);
    } catch (e) {
      console.error('[favourites] error removing handles', e);
    }

    showEl(loadingEl, false);
    showEl(contentEl, false);
    showEl(emptyEl, true);
    showEl(gridWrapper, false);
    showEl(loadMoreWrapper, false);
    document.querySelectorAll('.wishlist-header-link .wkh-counter, .wishlist-header-link .mai-wishlist-count-overlay').forEach(function (el) { el.style.display = 'none'; });
  }

  function shouldRemoveWishlistItemAfterCartAttempt(result) {
    if (!result) return false;
    if (result.reason === 'already_in_cart') return true;
    if (result.ok) return true;
    return result.status === 422;
  }

  function redirectToCart() {
    window.location.href = (getLocaleRoot() || '') + '/cart';
  }

  function fetchWishlistPublicId() {
    if (window.__favouritesWishlistPublicId) {
      return Promise.resolve(window.__favouritesWishlistPublicId);
    }

    if (wishlistPublicIdPromise) {
      return wishlistPublicIdPromise;
    }

    if (!window.__wishlistShareCustomerId || !window.Shopify || !window.Shopify.shop) {
      return Promise.resolve(null);
    }

    wishlistPublicIdPromise = fetch('https://api.appmate.io/v2/wishlists/mine', {
      method: 'GET',
      headers: getWishlistShareRequestHeaders()
    })
      .then(function (res) {
        return res.ok ? res.json() : null;
      })
      .then(function (json) {
        var publicId = json && json.wishlist && typeof json.wishlist.publicId === 'string'
          ? json.wishlist.publicId
          : null;

        if (publicId) {
          window.__favouritesWishlistPublicId = publicId;
        }

        return publicId;
      })
      .catch(function () {
        return null;
      })
      .then(function (publicId) {
        if (!publicId) {
          wishlistPublicIdPromise = null;
        }
        return publicId;
      });

    return wishlistPublicIdPromise;
  }

  function buildWishlistShareUrl(publicId) {
    if (!publicId) return null;

    var path = '/apps/wishlist/' + encodeURIComponent(publicId);
    var params = {
      utm_source: 'share',
      utm_medium: 'creator',
      utm_content: window.__wishlistShareCustomerId || '',
      n: window.__wishlistShareCustomerName || ''
    };
    var search = Object.keys(params)
      .filter(function (k) { return params[k] !== undefined && params[k] !== null && params[k] !== ''; })
      .map(function (k) { return encodeURIComponent(k) + '=' + encodeURIComponent(params[k]); })
      .join('&');
    var origin = window.location.origin;

    return search ? origin + path + '?' + search : origin + path;
  }

  function getWishlistShareUrl() {
    return fetchWishlistPublicId().then(function (publicId) {
      return buildWishlistShareUrl(publicId);
    });
  }

  function initFavouritesAddAllButton(root) {
    var sectionId = root && root.dataset && root.dataset.sectionId;
    if (!sectionId) return;

    var btn = document.getElementById('favourites-add-all-to-cart-' + sectionId);
    if (!btn) return;
    var spinner = btn.querySelector('.loading-overlay__spinner');

    btn.addEventListener('click', function () {
      if (btn.disabled) return;

      var wishlistPath = root.dataset.wishlistApiPath || '/apps/wishlist/api/items';

      btn.disabled = true;
      btn.classList.add('loading');
      if (spinner) {
        spinner.classList.remove('hidden');
      }

      Promise.all([
        fetchWishlistItems(wishlistPath),
        getCartVariantIdSet()
      ]).then(function (results) {
        var wishlistData = results[0];
        var cartVariantIds = results[1];
        var wishlistItems = extractWishlistItems(wishlistData);

        if (!wishlistItems.length) {
          redirectToCart();
          return;
        }

        var itemsToRemove = [];
        var sequence = Promise.resolve();

        wishlistItems.forEach(function (item) {
          sequence = sequence.then(function () {
            var wishlistItemId = item && item.id;
            var variantId = item && item.variantId;

            if (!wishlistItemId) {
              return;
            }

            if (!variantId) {
              return;
            }

            if (cartVariantIds.has(String(variantId))) {
              itemsToRemove.push(wishlistItemId);
              return;
            }

            return addVariantToCart(variantId, item.quantity).then(function (result) {
              if (result && result.ok) {
                cartVariantIds.add(String(variantId));
              }

              if (shouldRemoveWishlistItemAfterCartAttempt(result)) {
                itemsToRemove.push(wishlistItemId);
              }
            });
          });
        });

        return sequence.then(function () {
          var removeSequence = Promise.resolve();

          itemsToRemove.forEach(function (wishlistItemId) {
            removeSequence = removeSequence.then(function () {
              return removeWishlistItemById(wishlistItemId);
            });
          });

          return removeSequence.then(function () {
            redirectToCart();
          });
        });
      }).catch(function (error) {
        console.warn('[favourites] Add all to cart failed', error);
        redirectToCart();
      }).finally(function () {
        btn.disabled = false;
        btn.classList.remove('loading');
        if (spinner) {
          spinner.classList.add('hidden');
        }
      });
    });
  }

  function initFavouritesClearButton(root) {
    var sectionId = root && root.dataset && root.dataset.sectionId;
    if (!sectionId) return;

    var btn = document.getElementById('favourites-clear-' + sectionId);
    if (!btn) return;
    var spinner = btn.querySelector('.loading-overlay__spinner');

    btn.addEventListener('click', function () {
      if (btn.disabled) return;

      btn.disabled = true;
      btn.classList.add('loading');
      if (spinner) {
        spinner.classList.remove('hidden');
      }

      clearWishlist().then(function (success) {
        if (!success) {
          console.warn('[favourites] Clear wishlist failed');
          return;
        }

        showFavouritesEmptyState();
      }).finally(function () {
        btn.disabled = false;
        btn.classList.remove('loading');
        if (spinner) {
          spinner.classList.add('hidden');
        }
      });
    });
  }

  function initFavouritesShareButton(root) {
    var sectionId = root && root.dataset && root.dataset.sectionId;
    if (!sectionId) return;
    var btn = document.getElementById('favourites-share-' + sectionId);
    if (!btn) return;

    fetchWishlistPublicId();

    btn.addEventListener('click', function () {
      if (btn.disabled) return;

      btn.disabled = true;
      getWishlistShareUrl().then(function (url) {
        if (!url) {
          console.warn('[favourites] Unable to build wishlist share URL');
          return;
        }

        if (typeof navigator !== 'undefined' && navigator.share && typeof navigator.share === 'function') {
          return navigator.share({ url: url, title: document.title }).catch(function () {
            copyShareUrlFallback(url, btn);
          });
        }

        copyShareUrlFallback(url, btn);
      }).finally(function () {
        btn.disabled = false;
      }).catch(function () {
        btn.disabled = false;
        console.warn('[favourites] Wishlist share failed');
      });
    });
  }

  function copyShareUrlFallback(url, btn) {
    var message = (typeof window.accessibilityStrings !== 'undefined' && window.accessibilityStrings.shareSuccess)
      ? window.accessibilityStrings.shareSuccess
      : 'Link copied to clipboard';
    if (navigator.clipboard && typeof navigator.clipboard.writeText === 'function') {
      navigator.clipboard.writeText(url).then(function () {
        if (typeof window.showConfirmationToast === 'function') {
          window.showConfirmationToast(message);
        }
      }).catch(function () {
        fallbackCopyToClipboard(url, message, btn);
      });
    } else {
      fallbackCopyToClipboard(url, message, btn);
    }
  }

  function fallbackCopyToClipboard(url, message, btn) {
    var input = document.createElement('input');
    input.value = url;
    input.setAttribute('readonly', '');
    input.style.position = 'absolute';
    input.style.left = '-9999px';
    document.body.appendChild(input);
    input.select();
    try {
      document.execCommand('copy');
      if (typeof window.showConfirmationToast === 'function') {
        window.showConfirmationToast(message);
      }
    } catch (e) {
      console.error('[favourites] error copying to clipboard', e);
    }
    document.body.removeChild(input);
    if (btn && btn.focus) btn.focus();
  }

  /**
   * When user removes an item from wishlist (wk-button loses wk-selected) inside this section,
   * remove that card from the DOM and update stored handles (no API roundtrip).
   */
  function startFavouritesRemoveObserver(root) {
    var observer = new MutationObserver(function (mutations) {
      for (var i = 0; i < mutations.length; i++) {
        var target = mutations[i].target;
        if (!target || !target.classList || !target.classList.contains('wk-button')) continue;
        if (!root.contains(target)) continue;
        var hadSelected = mutations[i].oldValue && mutations[i].oldValue.indexOf('wk-selected') !== -1;
        var hasSelected = target.classList.contains('wk-selected');
        if (hadSelected && !hasSelected) {
          removeCardFromFavouritesGrid(target);
          break;
        }
      }
    });
    observer.observe(root, {
      subtree: true,
      attributes: true,
      attributeFilter: ['class'],
      attributeOldValue: true
    });
  }

  function loadFavourites() {
    var root = getSectionRoot();
    if (!root) return;

    var gridEl = getEl('grid');
    if (!gridEl) return;

    console.debug('[favourites] init');

    initFavouritesAddAllButton(root);
    initFavouritesClearButton(root);
    initFavouritesShareButton(root);
    startFavouritesRemoveObserver(root);
    runFetchAndRender(root);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', loadFavourites);
  } else {
    loadFavourites();
  }
})();
